import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, srcSet, type ShopListItem } from '../api';
import { formatPriceRange } from '../money';

/**
 * Hledani v hlavicce s naseptavacem.
 *
 * Hledany vyraz zije v URL, ne ve stavu komponenty – odkaz na vysledek
 * musi jit poslat a zpetne tlacitko musi fungovat. Odeslani prepne na
 * domovskou stranku, aby slo hledat i z detailu produktu nebo z kosiku.
 *
 * Naseptavac je zkratka, ne jedina cesta: kdyz se nenacte nebo v nem
 * zakaznik nic nenajde, obycejne odeslani formulare porad funguje.
 */

/** Po kolika znacích má smysl se ptát serveru. */
const MIN_QUERY = 2;
/** Prodleva při psaní. Kratší by znamenala dotaz na každé písmeno. */
const DEBOUNCE_MS = 220;
const SUGGESTION_LIMIT = 6;

export function SearchBox() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const listId = useId();

  const [term, setTerm] = useState(params.get('hledat') ?? '');
  const [items, setItems] = useState<readonly ShopListItem[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading] = useState(false);

  const wrap = useRef<HTMLDivElement | null>(null);
  // Vzdy drzi to, co je PRAVE v poli. Odpoved se aplikuje jen tehdy, kdyz
  // se ji dotazany vyraz jeste shoduje s aktualnim – citac by nestacil:
  // pomala odpoved na stary vyraz projde, dokud novy dotaz jeste neodesel.
  const latestQuery = useRef('');

  // Kdyz se vyraz zmeni jinde (klik na kategorii, zpetne tlacitko),
  // policko musi jit s nim.
  useEffect(() => {
    setTerm(params.get('hledat') ?? '');
    setOpen(false);
  }, [params]);

  useEffect(() => {
    const query = term.trim();
    latestQuery.current = query;
    if (query.length < MIN_QUERY) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const timer = window.setTimeout(() => {
      api
        .listProducts({ search: query, limit: SUGGESTION_LIMIT })
        .then((result) => {
          // Aplikuj jen kdyz je to porad ten vyraz, co je v poli. Pomala
          // odpoved na predchozi vyraz se tak zahodi, i kdyz mezitim jeste
          // nedobehl novy dotaz – citac by ji pustil, protoze by se s nim
          // shodovala.
          if (latestQuery.current !== query) return;
          setItems(result.items);
          setHighlight(-1);
        })
        .catch(() => {
          if (latestQuery.current === query) setItems([]);
        })
        .finally(() => {
          if (latestQuery.current === query) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    if (!open) return undefined;
    function onPointer(event: MouseEvent) {
      if (wrap.current !== null && !wrap.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setOpen(false);
    const next = new URLSearchParams();
    if (term.trim() !== '') next.set('hledat', term.trim());
    const query = next.toString();
    void navigate(query === '' ? '/' : `/?${query}`);
  }

  function openProduct(product: ShopListItem) {
    setOpen(false);
    void navigate(`/produkt/${product.slug}`);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (items.length === 0) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => {
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = current + step;
        // Za posledni polozkou i pred prvni je stav "nic nevybrano" (-1).
        // Enter pak odesle cely dotaz misto otevreni produktu.
        if (next >= items.length) return -1;
        if (next < -1) return items.length - 1;
        return next;
      });
      return;
    }

    if (event.key === 'Enter' && highlight >= 0 && highlight < items.length) {
      const picked = items[highlight];
      if (picked !== undefined) {
        event.preventDefault();
        openProduct(picked);
      }
    }
  }

  const showPanel = open && term.trim().length >= MIN_QUERY;

  return (
    <div className="header-search" ref={wrap}>
      <form role="search" onSubmit={submit}>
        <label className="visually-hidden" htmlFor="hledani">
          Hledat zboží
        </label>
        <input
          id="hledani"
          type="search"
          autoComplete="off"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-autocomplete="list"
          placeholder="Co hledáte? Granule, stelivo, hračky…"
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <button type="submit" aria-label="Hledat">
          <SearchIcon />
          <span className="search-label">Hledat</span>
        </button>
      </form>

      {showPanel && (
        <div className="suggest" id={listId} role="listbox" aria-label="Našeptávač">
          {items.map((product, index) => (
            <button
              key={product.id}
              type="button"
              role="option"
              aria-selected={index === highlight}
              className={index === highlight ? 'suggest-item is-active' : 'suggest-item'}
              onMouseEnter={() => setHighlight(index)}
              onClick={() => openProduct(product)}
            >
              <span className="suggest-thumb">
                {product.imageUrl === null ? (
                  <span aria-hidden="true">{product.name.slice(0, 1)}</span>
                ) : (
                  <img
                    src={product.imageUrl}
                    srcSet={srcSet(product.imageThumbnails)}
                    sizes="44px"
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </span>
              <span className="suggest-body">
                {product.brand !== null && <span className="muted small">{product.brand}</span>}
                <strong>{product.name}</strong>
              </span>
              <span className="suggest-price">
                {formatPriceRange(product.priceFromMinor, product.priceToMinor)}
              </span>
            </button>
          ))}

          {items.length === 0 && (
            <p className="suggest-empty muted small">
              {loading ? 'Hledám…' : 'Nic jsme nenašli. Zkus jiné slovo.'}
            </p>
          )}

          {items.length > 0 && (
            <button type="button" className="suggest-all" onClick={submit}>
              Zobrazit všechny výsledky pro „{term.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path
        d="m16 16 4.5 4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
